import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { modelClass, botModes, botStatuses, defaultConfig, eagerInvite, botMaintenance, startBot, stopBot, Bot, BotConfig, StoreType, BotMaintenanceAction } from '/imports/api/bots';
import { eagerModes, baseBehaviourMode, type BotMode } from '/imports/api/botModes';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { Select } from '/imports/ui/Select';
import { Input } from '/imports/ui/Input';
import { NumberInput } from '/imports/ui/NumberInput';
import { Checkbox } from '/imports/ui/Checkbox';
import { InheritToggle } from '/imports/ui/InheritToggle';
import { TextArea } from '/imports/ui/TextArea';
import { Button } from '/imports/ui/Button';
import { FormBuilder } from '/imports/ui/FormBuilder';
import { JsonField, validateMongoQuery } from '/imports/ui/JsonField';
import { InventoryPolicyPicker } from '/imports/ui/InventoryPolicyPicker';
import { QueryPreview } from '/imports/ui/QueryPreview';
import { Page } from '/imports/ui/Page';

const statusOptions = botStatuses.map((s) => ({ value: s, label: s }));
const modeOptions = botModes.map((m) => ({ value: m, label: m }));

const storeTypes: StoreType[] = ['redeemable', 'nonredeemable', 'pauper', 'common', 'rare', 'booster', 'legacy', 'foil', 'promo'];
const storeTypeOptions = storeTypes.map((v) => ({ value: v, label: v }));

const configFormSpec: any[][] = [
  [{ field: 'storetype', label: 'Store Type', input: { type: Select, props: { options: storeTypeOptions } } }],
  [{ field: 'shelfsize', label: 'Shelf Size', input: { type: NumberInput } }, { field: 'stocksize', label: 'Stock Size', input: { type: NumberInput } }],
  [{ field: 'selllimit', label: 'Sell Limit', input: { type: NumberInput } }, { field: 'mintix', label: 'Min Tix', input: { type: NumberInput } }],
  [{ field: 'adjBuyPrice', label: 'Adj Buy Price', input: { type: NumberInput } }, { field: 'adjSellPrice', label: 'Adj Sell Price', input: { type: NumberInput } }],
  [{ field: 'lifespan', label: 'Lifespan (hours)', input: { type: NumberInput } }],
  [{
    field: 'replenishPriority', label: 'Replenish Priority', input: { type: NumberInput },
    tip: 'Higher number replenishes first at the shared depots. Blank inherits the fleet\'s priority, then Settings, then 2.',
  }],
  [
    { field: 'defaultBuyPrice', label: 'Default Buy Price (junk floor)', input: { type: NumberInput } },
    { field: 'defaultSellPrice', label: 'Default Sell Price (junk floor)', input: { type: NumberInput } },
  ],
  [{
    field: 'classifiedQuery',
    input: (setUpdate: any, _update: any, document: any) => (
      <JsonField<BotConfig>
        field="classifiedQuery"
        label="Classified Query (override)"
        tip={'COMPLETELY replaces this bot\u2019s fleet/Settings classified card list when set. Shared list edits then no longer affect this bot. Clear to re-inherit.'}
        initial={document?.classifiedQuery}
        validate={validateMongoQuery}
        emptyValue={null}
        setUpdate={setUpdate}
        className="h-24! font-mono text-caption"
      />
    ),
  }],
  [{
    field: 'classifiedQuery',
    input: (_setUpdate: any, update: any, document: any) => (
      <QueryPreview
        query={'classifiedQuery' in ((update ?? {}) as object) ? update.classifiedQuery : document?.classifiedQuery}
        emptyHint="No override — inherits the fleet/Settings card list."
      />
    ),
  }],
  [{
    field: 'buyQuery',
    input: (setUpdate: any, _update: any, document: any) => (
      <JsonField<BotConfig>
        field="buyQuery"
        label="Buy Query (override)"
        tip={'Complete replacement for the fleet/Settings buy card list, this bot only.'}
        initial={document?.buyQuery}
        validate={validateMongoQuery}
        emptyValue={null}
        setUpdate={setUpdate}
        className="h-24! font-mono text-caption"
      />
    ),
  }],
  [{
    field: 'buyQuery',
    input: (_setUpdate: any, update: any, document: any) => (
      <QueryPreview
        query={'buyQuery' in ((update ?? {}) as object) ? update.buyQuery : document?.buyQuery}
        emptyHint="No override — inherits the fleet/Settings card list."
      />
    ),
  }],
  [{
    field: 'sellQuery',
    input: (setUpdate: any, _update: any, document: any) => (
      <JsonField<BotConfig>
        field="sellQuery"
        label="Sell Query (override)"
        tip={'Complete replacement for the fleet/Settings sell card list, this bot only.'}
        initial={document?.sellQuery}
        validate={validateMongoQuery}
        emptyValue={null}
        setUpdate={setUpdate}
        className="h-24! font-mono text-caption"
      />
    ),
  }],
  [{
    field: 'sellQuery',
    input: (_setUpdate: any, update: any, document: any) => (
      <QueryPreview
        query={'sellQuery' in ((update ?? {}) as object) ? update.sellQuery : document?.sellQuery}
        emptyHint="No override — inherits the fleet/Settings card list."
      />
    ),
  }],
  [{
    field: 'inventoryPolicyId',
    input: (setUpdate: any, update: any, document: any) => (
      <div className="json-field">
        <label>Inventory Policy (override)</label>
        <div className="mb-1 text-caption text-fg-3">
          Per-card stock quantities for this bot only. Replaces the fleet’s policy when set.
        </div>
        <InventoryPolicyPicker
          value={'inventoryPolicyId' in ((update ?? {}) as object) ? update.inventoryPolicyId : document?.inventoryPolicyId}
          onChange={(id) => setUpdate((prev: any) => ({ ...prev, inventoryPolicyId: id }))}
          inheritLabel="Inherit from fleet"
        />
      </div>
    ),
  }],
  [{ field: 'classified', label: 'Classified', input: { type: TextArea } }],
  [{ field: 'classifiedFile', label: 'Classified File', input: { type: Input } }],
  [{ field: 'hoard', label: 'Hoard', input: { type: Checkbox } }],
  [{
    field: 'overdraft',
    input: (setUpdate: any, update: any, document: any) => (
      <InheritToggle
        label="Overdraft (buy on credit)"
        hint="Keep buying once this bot's tickets run out, settling the shortfall as store credit. Inherit follows the fleet, then Settings."
        value={'overdraft' in ((update ?? {}) as object) ? update.overdraft : document?.overdraft}
        onChange={(v) => setUpdate((prev: any) => ({ ...prev, overdraft: v }))}
        inheritLabel="Inherit from fleet / Settings"
        yesLabel="Yes — buy past the ticket balance"
        noLabel="No — stop when tickets run out"
      />
    ),
  }],
  [{ field: 'replenish', label: 'Replenish', input: { type: Checkbox } }],
  [{ field: 'depleteTix', label: 'Deplete Tix', input: { type: Checkbox } }],
  [{ field: 'depleteCards', label: 'Deplete Cards', input: { type: Checkbox } }],
  [{ field: 'scrape', label: 'Scrape', input: { type: Checkbox } }],
  [{ field: 'silent', label: 'Silent', input: { type: Checkbox } }],
  [{ field: 'bulk', label: 'Bulk', input: { type: Checkbox } }],
  [{ field: 'expensivetaste', label: 'Expensive Taste', input: { type: Checkbox } }],
  [{ field: 'parseUsers', label: 'Parse Users', input: { type: Checkbox } }],
  [{ field: 'advertise', label: 'Advertise (uncheck to hide from marketplace)', input: { type: Checkbox } }],
  [{ field: 'advertisefoils', label: 'Advertise Foils', input: { type: Checkbox } }],
  [{ field: 'log', label: 'Log to DB', input: { type: Checkbox } }],
  [{
    field: 'engineered', label: 'Engineered trades (negotiate eager trades to completion)', input: { type: Checkbox },
    tip: 'Eager-initiated trades only: answer partner ticket corrections at their bid when it clears our buy price, use their keymaps, submit on handoffs. Off = exactly the old behaviour.',
  }],
];

const ConfigSubForm = ({
  config, setConfig, document,
}: {
  config: Partial<BotConfig>;
  setConfig: (action: React.SetStateAction<Partial<BotConfig>>) => void;
  document?: BotConfig;
}) => (
  <FormBuilder<BotConfig>
    form={configFormSpec}
    document={document}
    update={config}
    setUpdate={setConfig}
    error={{}}
  />
);

// Tailwind shell replacing the old styled(Page) wrapper passed as ModelFormPage's
// `styled` prop. It replaces ModelFormPageStyled, so it must re-assert that
// default's `form { padding-bottom: 3rem }` rule, plus its own .additional-config.
const BotPageShell = (props: React.ComponentProps<typeof Page>) => (
  <Page
    {...props}
    className={[
      props.className ?? '',
      '[&_form]:pb-12',
      '[&_.additional-config]:mb-8',
    ].join(' ')}
  />
);

/**
 * Manual eager-trade trigger, shown only for eager-mode bots. Fires the same
 * gauntlet the cron runs (roster + blacklist gates; only the hourly budget
 * is waived) -- the outcome lands in the bot's console log on the Bots page,
 * since the method can only report "queued".
 */
const EagerInvitePanel = ({ username }: { username: string }) => {
  const [target, setTarget] = useState('');
  const invite = async () => {
    try {
      await eagerInvite.call({ username, target: target.trim() || undefined });
      toast.success(target.trim()
        ? `Invitation to ${target.trim()} queued — outcome in the console log`
        : 'Invitation queued (marketplace pick) — outcome in the console log');
    }
    catch (error) {
      toast.error(`${error}`);
    }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 rounded border border-line p-3">
      <span className="font-semibold">Eager trade</span>
      <Input
        placeholder="muser to invite (blank = pick from marketplace)"
        value={target}
        onChange={(v) => setTarget(v ?? '')}
        className="min-w-72"
      />
      <Button type="button" className="btn primary" onClick={() => void invite()}>
        Invite to trade
      </Button>
    </div>
  );
};

/** Start/Stop for this one bot -- the same desired-state methods the Bots
 *  page rows use, so intent is recorded server-side and survives the tab. */
const RunPanel = ({ username }: { username: string }) => {
  const start = async () => {
    try {
      await startBot.call({ username });
      toast.success(`${username} start queued`);
    }
    catch (error) {
      toast.error(`${error}`);
    }
  };
  const stop = async () => {
    try {
      await stopBot.call({ username });
      toast.success(`${username} stop requested`);
    }
    catch (error) {
      toast.error(`${error}`);
    }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 rounded border border-line p-3">
      <span className="font-semibold">Run</span>
      <Button type="button" className="btn primary" onClick={() => void start()}>Start</Button>
      <Button type="button" className="btn" onClick={() => void stop()}>Stop</Button>
    </div>
  );
};

/**
 * Manual housekeeping triggers. Each queues one immediate deplete/replenish
 * leg (option gates and depot backoff waived); the outcome lands in the
 * bot's console log. Buttons show only for the base modes they apply to.
 */
const MaintenancePanel = ({ username, mode }: { username: string; mode: BotMode }) => {
  const base = baseBehaviourMode(mode);
  const fire = async (action: BotMaintenanceAction, label: string) => {
    try {
      await botMaintenance.call({ username, action });
      toast.success(`${label} queued — outcome in the console log`);
    }
    catch (error) {
      toast.error(`${error}`);
    }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 rounded border border-line p-3">
      <span className="font-semibold">Housekeeping</span>
      <Button type="button" className="btn" onClick={() => void fire('depleteTix', 'Deplete Tix')}>
        Deplete Tix
      </Button>
      {(base === 'buy' || base === 'shred' || base === 'sell' || base === 'hybrid') && (
        // Buy/shred hand over everything; sell/hybrid only what sits above their stock targets, dead stock included.
        <Button type="button" className="btn" onClick={() => void fire('depleteCards', 'Deplete Cards')}>
          Deplete Cards
        </Button>
      )}
      {(base === 'sell' || base === 'hybrid') && (
        <Button type="button" className="btn" onClick={() => void fire('replenishCards', 'Replenish Cards')}>
          Replenish Cards
        </Button>
      )}
    </div>
  );
};

export const BotPage = () => {
  const [showConfig, setShowConfig] = useState(false);
  const [document, setDocument] = useState<Bot | undefined>();
  return (
    <ModelFormPage
      modelClass={modelClass as any}
      styled={BotPageShell}
      permission={(user) => !!user?.isSuperUser}
      setDocument={setDocument as any}
      formSpec={() => [
        [{ field: 'username', label: 'Username' }],
        [{ field: 'password', label: 'Password' }],
        [{ field: 'mfakey', label: 'MFA Key' }],
        [{ field: 'mode', label: 'Mode', input: { type: Select, props: { options: modeOptions } } }],
        [{ field: 'active', label: 'Active' }],
        [{ field: 'status', label: 'Status', input: { type: Select, props: { options: statusOptions } } }],
      ]}
      footer={(setUpdate, update) => (
        <>
          <Button
            type="button"
            className="btn primary additional-config"
            onClick={() => setShowConfig((s) => !s)}
          >
            {showConfig ? '− Hide Additional Config' : '+ Additional Config'}
          </Button>
          {showConfig && (
            <ConfigSubForm
              // Pre-fill effective values: the bot merges defaultConfig under
              // its stored config at runtime, so the form should show the same.
              document={{ ...defaultConfig, ...(document?.config as Partial<BotConfig> | undefined) }}
              config={((update as any).config || {}) as Partial<BotConfig>}
              setConfig={(action) => {
                setUpdate((prev: any) => {
                  const prevConfig: Partial<BotConfig> = prev.config || {};
                  const next = typeof action === 'function'
                    ? (action as (p: Partial<BotConfig>) => Partial<BotConfig>)(prevConfig)
                    : action;
                  return { ...prev, config: next };
                });
              }}
            />
          )}
          {document?.username && <RunPanel username={document.username} />}
          {document?.username && eagerModes.includes(document.mode) && (
            <EagerInvitePanel username={document.username} />
          )}
          {document?.username && document.mode && (
            <MaintenancePanel username={document.username} mode={document.mode} />
          )}
        </>
      )}
    />
  );
};
